// The view side of a room the hero walks into (gate C4), shared by the places lab (view.ts) and the streamed valley
// (src/world/places-view.ts):
//   - the camera: the room's profile requested on its table layer, and the camera stops colliding with buildings (it
//     looks in over the cut walls); leaving releases only that request (a lock-on or combat request on the same layer
//     stays) and gives the camera its collision back;
//   - the cut-away: the room's roof and the walls facing the camera dissolve on the kit surface (`setKitCut`), fading
//     in over `cutFade` s inside and out again outside.
// The light (the lab's stand-in or the sky's indoor look) is the caller's.
import { setKitCut, type KitUniforms } from "@rpg/look";
import { cutMask, type InteriorDesc, type KitPlacement } from "@rpg/meshkit";
import type { ValleyCamera } from "../valley/camera";
import { CAMERA_LAYERS } from "../valley/camera";
import { PLACES_FAULTS } from "./faults";

/** A room as the view needs it: its owner number in the kit batch, its interior and where it stands. */
export interface ViewRoom {
  owner: number;
  interior: InteriorDesc;
  place: KitPlacement;
}

/** The camera's side of a room: request its profile and drop the buildings from the camera's collision on entering;
 *  release only that request and restore the collision on leaving. */
export function interiorCamera(camera: ValleyCamera | null) {
  let requested: string | null = null;
  let solid = true;
  return {
    enter(profile: string): void {
      if (camera && !PLACES_FAULTS["places-no-profile"]) {
        camera.request(profile);
        requested = profile;
      }
      if (camera?.rig.collision) {
        camera.rig.collision = { ...camera.rig.collision, layers: ["terrain"] };
        solid = false;
      }
    },
    exit(): void {
      if (camera && requested) {
        // only the layer that still holds the room's profile: another system's request on that layer stays
        const layers = camera.rig.profiles.state().layers;
        for (const [layer, name] of Object.entries(layers)) if (name === requested) camera.release(layer);
      }
      requested = null;
      if (camera?.rig.collision) {
        camera.rig.collision = { ...camera.rig.collision, layers: CAMERA_LAYERS };
        solid = true;
      }
    },
    /** Whether the camera collides with buildings now (false inside a room). */
    solid: () => solid,
  };
}

/** The cut-away on the kit surface: call `frame` every drawn frame with the room the hero is in (or null). */
export function interiorCut(o: { kit: KitUniforms; fade: number; margin: number }) {
  let cut = { owner: -1, mask: 0, fade: 0 };
  return {
    frame(room: ViewRoom | null, eye: { x: number; z: number }, dt: number): void {
      if (room) {
        if (cut.owner !== room.owner) cut = { owner: room.owner, mask: 0, fade: 0 };
        cut.mask = cutMask(room.interior, room.place, eye, -o.margin);
        cut.fade = Math.min(1, cut.fade + dt / Math.max(1e-3, o.fade));
      } else if (cut.owner >= 0) {
        cut.fade = Math.max(0, cut.fade - dt / Math.max(1e-3, o.fade));
        if (cut.fade === 0) cut = { owner: -1, mask: 0, fade: 0 };
      }
      setKitCut(o.kit, cut.owner, cut.mask, cut.fade);
    },
    state: () => ({ ...cut }),
  };
}
